// client/src/components/CashierDashboard.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { firestore } from '../../firebase';
import {
  doc, updateDoc, collection, query, where, getDocs, onSnapshot,
  deleteField, getDoc, arrayUnion,
} from 'firebase/firestore';
import {
  getAuth, onAuthStateChanged
} from 'firebase/auth';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaTrash, FaWifi, FaShoppingCart, FaGift } from 'react-icons/fa';
import CashierNavbar from './CashierNavbar';
import { sanitize } from '../../utils/sanitize';

/* ───────────── Helpers précision décimale (centimes) ───────────── */
const parseFlexibleNumber = (val) => {
  if (val === null || val === undefined) return 0;
  const s = String(val).replace(',', '.').trim();
  const n = parseFloat(s);
  return isFinite(n) ? n : 0;
};
const toCents = (n) => Math.round(parseFlexibleNumber(n) * 100);
const fromCents = (cents) => parseFloat((Math.round(cents) / 100).toFixed(2));
const formatPts = (n) =>
  parseFlexibleNumber(n).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// ------ FORMAT DATE (desktop compact / mobile dd/mm/aa) ------
const formatCompactDateFR = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const WD = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const MO = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
  const wd = WD[dateObj.getDay()];
  const d = dateObj.getDate();
  const m = MO[dateObj.getMonth()];
  const y = dateObj.getFullYear();
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mm = String(dateObj.getMinutes()).padStart(2, '0');
  return `${wd} ${d} ${m} ${y} ${hh}:${mm}`;
};

// ✅ Mobile: date courte dd/mm/aa
const formatDDMMYY = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const d = String(dateObj.getDate()).padStart(2, '0');
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const y = String(dateObj.getFullYear()).slice(-2);
  return `${d}/${m}/${y}`;
};

// ─── Confirme que les writes ont bien été commit côté serveur ──────────────────
const waitForCommit = (refDoc) => new Promise((resolve, reject) => {
  try {
    const unsub = onSnapshot(refDoc, { includeMetadataChanges: true }, (snap) => {
      if (!snap.exists()) return;
      if (!snap.metadata.hasPendingWrites) { unsub(); resolve(); }
    }, (e) => { try { if (unsub) unsub(); } catch {} reject(e); });
  } catch (e) { reject(e); }
});

// ─── Hook utilitaire: détecter mobile ──────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
};

// ✅ Nouveau: détecter la tranche 1059px → 771px
const useIsMid = () => {
  const [isMid, setIsMid] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1059px) and (min-width: 771px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 1059px) and (min-width: 771px)');
    const handler = (e) => setIsMid(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMid;
};

const CashierDashboard = () => {
  /* ──────────────── États ──────────────── */
  const [user, setUser] = useState(null);
  // eslint-disable-next-line
  const [photos, setPhotos] = useState([]);
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [invitations, setInvitations] = useState([]);
  // eslint-disable-next-line
  const [showModal, setShowModal] = useState(false);
  const [showIncrementModal, setShowIncrementModal] = useState(false);
  const [tempIncrement, setTempIncrement] = useState(1);
  const [fidelityHistory, setFidelityHistory] = useState([]);
  const [codeInput, setCodeInput] = useState('');
  const [result, setResult] = useState(null); // "success" | "error" | "offline"
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [userPseudos, setUserPseudos] = useState({});
  const [fidelityIncrement, setFidelityIncrement] = useState(1);
  const [multiScanEnabled, setMultiScanEnabled] = useState(false);
  const [pendingCodes, setPendingCodes] = useState([]);

  // Connectivité
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  // Fidélité par points
  const [fidelityType, setFidelityType] = useState(null);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [amountInput, setAmountInput] = useState("");
  const [customerPoints, setCustomerPoints] = useState(0);
  // eslint-disable-next-line
  const [palierAtteint, setPalierAtteint] = useState(null);
  const [customerDocRef, setCustomerDocRef] = useState(null);
  const [pointsPaliers, setPointsPaliers] = useState([]);

  const [showGainsModal, setShowGainsModal] = useState(false);
  const [currentCustomerGains, setCurrentCustomerGains] = useState([]);
  const [currentCustomerDocRef, setCurrentCustomerDocRef] = useState(null);
  // eslint-disable-next-line
  const [fidelityGift, setFidelityGift] = useState('');
  const successTimeoutRef = React.useRef(null);

  const triggerSuccess = () => {
    setResult('success');
    setShowSuccessAnimation(true);
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccessAnimation(false);
      setResult(null);
      successTimeoutRef.current = null;
    }, 2200);
  };

  const isMobile = useIsMobile();
  const isMid = useIsMid();

  /* ──────────────── Utilitaires ──────────────── */
  const verifyCodeExists = async () => {
    try {
      if (!user?.companyuid) return false;
      const companyId = user.companyuid;
      const listCollectionRef = collection(firestore, 'fidelity', companyId, 'list');
      const qy = query(listCollectionRef, where('code', '==', sanitize(codeInput.trim())));
      const snapshot = await getDocs(qy);
      if (!snapshot.empty) {
        const customerDoc = snapshot.docs[0];
        setCustomerDocRef(customerDoc.ref);
        const pts = customerDoc.data().points || 0;
        setCustomerPoints(fromCents(toCents(pts))); // normalise à 2 déc.
        return true;
      }
      return false;
    } catch (err) {
      console.error('Erreur verifyCodeExists :', err);
      return false;
    }
  };

  const handleOpenGainsModal = async (customerId) => {
    if (!user || !user.companyuid || !customerId) {
      setResult('error'); setTimeout(() => setResult(null), 4000);
      return;
    }
    try {
      const listCollectionRef = collection(firestore, 'fidelity', user.companyuid, 'list');
      const docRef = doc(listCollectionRef, customerId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return;
      const customerData = docSnap.data();
      const currentPoints = fromCents(toCents(customerData.points || 0));
      const paliersAtteints = pointsPaliers.filter(palier => currentPoints >= palier.points);
      if (paliersAtteints.length > 0) {
        setCurrentCustomerGains(paliersAtteints);
        setCurrentCustomerDocRef(docRef);
        setShowGainsModal(true);
      } else {
        alert("Ce client n'a pas encore de gains à utiliser.");
      }
    } catch (error) {
      console.error("Erreur lors de la vérification des gains :", error);
      setResult("error"); setTimeout(() => setResult(null), 4000);
    }
  };

  const handleUseGain = async (gain) => {
    if (!currentCustomerDocRef || !gain) return;
    try {
      const docSnap = await getDoc(currentCustomerDocRef);
      const currentPoints = fromCents(toCents(docSnap.data().points || 0));
      const newPointsTotal = fromCents(toCents(currentPoints) - toCents(gain.points));
      await updateDoc(currentCustomerDocRef, {
        points: newPointsTotal,
        pointshistory: arrayUnion({
          date: new Date(),
          uid: user.uid,
          points: fromCents(-toCents(gain.points)),
          gift: gain.gift
        })
      });
      await waitForCommit(currentCustomerDocRef);
      setShowGainsModal(false);
      setCurrentCustomerGains([]);
      setCurrentCustomerDocRef(null);
      triggerSuccess();
    } catch (error) {
      console.error("Erreur lors de l'utilisation du gain :", error);
      setResult("error"); setTimeout(() => setResult(null), 4000);
    }
  };

  const handleOpenIncrementModal = async () => {
    const exists = await verifyCodeExists();
    if (!exists) { setResult('error'); setTimeout(() => setResult(null), 4000); return; }
    if (fidelityType === 'Fidbypoints') setShowPointsModal(true);
    else if (multiScanEnabled) { setTempIncrement(fidelityIncrement); setShowIncrementModal(true); }
    else handleValider();
  };

  const handleConfirmIncrement = () => {
    const count = parseInt(tempIncrement, 10) || 1;
    setShowIncrementModal(false);
    handleValider(count);
    setFidelityIncrement(count);
  };

  // ✅ Fidélité par points — 100 centimes = 1 point
  const handleConfirmPoints = async () => {
    const normalizedInput = String(amountInput || '').replace(',', '.').trim();
    const amount = parseFlexibleNumber(normalizedInput);
    if (!amount || amount <= 0 || !customerDocRef) {
      setResult("error"); setTimeout(() => setResult(null), 4000); return;
    }
    if (!navigator.onLine) {
      setResult('offline'); setTimeout(() => setResult(null), 4000);
      return;
    }

    try {
      const addCents = toCents(amount);
      const currentCents = toCents(customerPoints);
      const newPointsTotal = fromCents(currentCents + addCents);

      // ⚡️ optimisation écriture : un seul updateDoc suffit (le doc existe)
      await updateDoc(customerDocRef, {
        clientUid: customerDocRef.id,
        code: deleteField(),
        points: newPointsTotal,
        pointshistory: arrayUnion({
          date: new Date(),
          uid: user.uid,
          points: fromCents(addCents),
          montant: parseFloat(amount.toFixed(2))
        })
      });

      // ✅ attend confirmation serveur
      await waitForCommit(customerDocRef);

      setCustomerPoints(newPointsTotal);
      setAmountInput("");
      setShowPointsModal(false);
      triggerSuccess();
      setCodeInput("");
    } catch (e) {
      console.error("Erreur handleConfirmPoints :", e);
      setResult("error"); setTimeout(() => setResult(null), 4000);
    }
  };

  const handleDeleteLastTransaction = async row => {
    if (!window.confirm("Êtes-vous certain de vouloir supprimer la dernière transaction ?")) return;
    if (!user?.companyuid) return;
    try {
      const companyId = user.companyuid;
      const docId = row.id.split(/-(?:ts|rs|ph)-/)[0];
      const docRef = doc(firestore, 'fidelity', companyId, 'list', docId);
      const docSnap = await getDoc(docRef);
      const data = docSnap.data() || {};

      // ⚡️ évite une lecture si on a déjà le type en mémoire
      let fType = fidelityType;
      let multiple = 5;
      if (!fType) {
        const fidelityDocSnap = await getDoc(doc(firestore, 'fidelity', companyId));
        fType = fidelityDocSnap.exists() ? fidelityDocSnap.data().Type : 'Fidbycount';
        multiple = parseInt(fidelityDocSnap.exists() ? fidelityDocSnap.data().multiple : 5, 10) || 5;
      } else {
        // on récupère le multiple si besoin
        const fidelityDocSnap = await getDoc(doc(firestore, 'fidelity', companyId));
        multiple = parseInt(fidelityDocSnap.exists() ? fidelityDocSnap.data().multiple : 5, 10) || 5;
      }

      if (fType === 'Fidbypoints') {
        const arr = data.pointshistory || [];
        if (arr.length === 0) return;
        const lastEntry = arr[arr.length - 1];
        const currentCents = toCents(data.points || 0);
        const lastCents = toCents(lastEntry.points || 0);
        const newPoints = fromCents(currentCents - lastCents);
        await updateDoc(docRef, { points: newPoints, pointshistory: arr.slice(0, -1) });
      } else {
        const key = row.type === 'timestamp' ? 'timestamps' : 'resetstamp';
        const arr = data[key] || [];
        if (arr.length === 0) return;
        const lastEntry = arr[arr.length - 1];
        const decrement = lastEntry.count || 1;
        const currentCompteur = data.compteur || 0;
        const newCompteur = key === 'resetstamp' ? currentCompteur + multiple - decrement : currentCompteur - decrement;
        await updateDoc(docRef, { compteur: newCompteur, [key]: arr.slice(0, -1) });
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la transaction', error);
    }
  };

  /* ──────────────── Hooks d'effets ──────────────── */
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  }, []);

  // Récupération des codes récents non validés (30 min) — même logique qu’avant
  useEffect(() => {
    if (!user?.companyuid) return;
    const listRef = collection(firestore, 'fidelity', user.companyuid, 'list');
    const unsubscribe = onSnapshot(listRef, snapshot => {
      const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000);
      const recentCodes = snapshot.docs
        .filter(d => {
          const data = d.data();
          if (!data.code || !data.createdAt) return false;
          const createdAtDate = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
          return createdAtDate >= thirtyMinAgo;
        })
        .map(d => sanitize(d.data().code));
      setPendingCodes(recentCodes);
    });
    return () => unsubscribe();
  }, [user]);

  // LocalStorage invitations
  useEffect(() => {
    const storedInvitations = localStorage.getItem('invitations');
    if (storedInvitations) setInvitations(JSON.parse(storedInvitations));
  }, []);

  // Chargement utilisateur + paramètres fidélité + historique live
  useEffect(() => {
    const auth = getAuth();
    let unsubUserDoc = null;
    let unsubHistory = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (userDoc) => {
      if (unsubUserDoc) { unsubUserDoc(); unsubUserDoc = null; }
      if (unsubHistory) { unsubHistory(); unsubHistory = null; }

      if (userDoc) {
        const userId = userDoc.uid;
        const userDocRef = doc(firestore, 'users', userId);

        unsubUserDoc = onSnapshot(userDocRef, async (docSnapshot) => {
          const userData = docSnapshot.data() || {};
          setUser({ ...userData, uid: userId, pseudo: sanitize(userData?.pseudo || '') });
          if (!userData?.pseudo) setShowModal(true);

          if (userData?.companyuid) {
            try {
              const fidelityDocRef = doc(firestore, 'fidelity', userData.companyuid);
              const fidelitySnap = await getDoc(fidelityDocRef);
              if (fidelitySnap.exists()) {
                const data = fidelitySnap.data();
                setMultiScanEnabled(data.multiscan === 'yes');
                setFidelityType(data.Type);
                setFidelityGift(data.gift || '');
                if (data.Type === 'Fidbypoints') {
                  setPointsPaliers([
                    { points: data.points1, gift: data.gift1 },
                    { points: data.points2, gift: data.gift2 },
                    { points: data.points3, gift: data.gift3 },
                  ].filter(p => p.points));
                }
              }

              const listCollectionRef = collection(firestore, 'fidelity', userData.companyuid, 'list');
              if (unsubHistory) { unsubHistory(); unsubHistory = null; }
              unsubHistory = onSnapshot(listCollectionRef, (snapshot) => {
                const historyData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                // Tri par date la plus récente sur les sous-évènements
                historyData.sort((a, b) => {
                  const toDate = (raw) => {
                    if (!raw) return new Date(0);
                    if (typeof raw.toDate === 'function') return raw.toDate();
                    if (raw instanceof Date) return raw;
                    return new Date(raw);
                  };
                  const lastDate = (item) => {
                    const lastTs = (item.timestamps || []).slice(-1)[0];
                    const lastPh = (item.pointshistory || []).slice(-1)[0];
                    const lastRs = (item.resetstamp || []).slice(-1)[0];
                    return new Date(Math.max(
                      lastTs ? toDate(lastTs.date).getTime() : 0,
                      lastPh ? toDate(lastPh.date).getTime() : 0,
                      lastRs ? toDate(lastRs.date).getTime() : 0
                    ));
                  };
                  return lastDate(b) - lastDate(a);
                });
                setFidelityHistory(historyData);
              });
            } catch (e) {
              console.error('Erreur fetchFidelityData / history snapshot :', e);
            }
          } else {
            if (unsubHistory) { unsubHistory(); unsubHistory = null; }
          }
        });
      } else {
        navigate('/logingeneral');
      }
    });

    return () => {
      if (unsubUserDoc) { unsubUserDoc(); }
      if (unsubHistory) { unsubHistory(); }
      unsubscribeAuth();
    };
  }, [navigate]);

  // 🔄 Écoute live des paramètres de fidélité
  useEffect(() => {
    if (!user?.companyuid) return;

    const fidelityDocRef = doc(firestore, 'fidelity', user.companyuid);
    const unsubscribe = onSnapshot(
      fidelityDocRef,
      (snap) => {
        if (!snap.exists()) {
          setFidelityType('Fidbycount');
          setMultiScanEnabled(false);
          setFidelityGift('');
          setPointsPaliers([]);
          return;
        }
        const data = snap.data() || {};
        setFidelityType(data.Type);
        setMultiScanEnabled(data.multiscan === 'yes');
        setFidelityGift(data.gift || '');
        if (data.Type === 'Fidbypoints') {
          const p1 = { points: parseFlexibleNumber(data.points1), gift: data.gift1 };
          const p2 = { points: parseFlexibleNumber(data.points2), gift: data.gift2 };
          const p3 = { points: parseFlexibleNumber(data.points3), gift: data.gift3 };
          setPointsPaliers([p1, p2, p3].filter(p => !!p.points && p.points > 0));
        } else {
          setPointsPaliers([]);
        }
      },
      (err) => {
        console.error('onSnapshot fidelity error:', err);
      }
    );

    return () => unsubscribe();
  }, [user?.companyuid]);

  // ─────────────── Historique aplati (puis filtré caisse = actions de ce caissier) ───────────────
  const flattenedHistory = useMemo(() => {
    const toDate = (raw) => {
      if (raw && typeof raw.toDate === 'function') return raw.toDate();
      if (raw instanceof Date) return raw;
      if (typeof raw === 'string') return new Date(raw);
      return new Date(0);
    };
    const rows = fidelityHistory.flatMap((item) => {
      const timestamps = (item.timestamps || []).map((ts, index) => ({
        id: `${item.id}-ts-${index}`, date: toDate(ts.date), uid: ts.uid || '', type: 'timestamp', count: ts.count
      }));
      const resetstamps = (item.resetstamp || []).map((rs, index) => ({
        id: `${item.id}-rs-${index}`, date: toDate(rs.date), uid: rs.uid || '', type: 'resetstamp', count: rs.count, giftAtTime: rs.giftAtTime,
      }));
      const pointshistory = (item.pointshistory || []).map((ph, index) => ({
        id: `${item.id}-ph-${index}`, date: toDate(ph.date), uid: ph.uid || '', type: 'pointshistory', points: ph.points, montant: ph.montant, gift: ph.gift
      }));
      return [...timestamps, ...resetstamps, ...pointshistory];
    });
    rows.sort((a, b) => b.date - a.date);
    return rows;
  }, [fidelityHistory]);

  // Map UID → pseudo
  useEffect(() => {
    const uniqueUids = Array.from(new Set(flattenedHistory.map(r => r.uid)));
    uniqueUids.forEach(uid => {
      if (uid && !(uid in userPseudos)) {
        const userDocRef = doc(firestore, 'users', uid);
        getDoc(userDocRef)
          .then(snap => {
            setUserPseudos(prev => ({
              ...prev,
              [uid]: snap.exists() ? sanitize(snap.data().pseudo) : uid
            }));
          })
          .catch(() => {
            setUserPseudos(prev => ({ ...prev, [uid]: uid }));
          });
      }
    });
  }, [flattenedHistory, userPseudos]);
  
  useEffect(() => () => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);
// eslint-disable-next-line
  const filteredHistory = user?.uid ? flattenedHistory.filter(row => row.uid === user.uid) : [];

  const handleNavigateToDetails = useCallback((customerId) => {
    if (!customerId) return;
    navigate(`/cashieranalysetransaction/${encodeURIComponent(customerId)}`);
  }, [navigate]);

  // ✅ Validation par scan (compte)
  const handleValider = async (overrideCount) => {
    if (!navigator.onLine) {
      setResult('offline'); setTimeout(() => setResult(null), 4000);
      return;
    }
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser || !user?.companyuid) {
        setResult('error'); setTimeout(() => setResult(null), 4000); return;
      }
      const companyId = user.companyuid;
      const fidelityRef = doc(firestore, 'fidelity', companyId);
      const fidelitySnap = await getDoc(fidelityRef);
      if (!fidelitySnap.exists()) { setResult('error'); setTimeout(() => setResult(null), 4000); return; }

      const { multiple = 5, gift: giftCurrent } = fidelitySnap.data();
      const increment = parseInt(overrideCount != null ? overrideCount : fidelityIncrement, 10) || 1;
      const listRef = collection(firestore, 'fidelity', companyId, 'list');
      const qy = query(listRef, where('code', '==', sanitize(codeInput.trim())));
      const snap = await getDocs(qy);
      if (snap.empty) { setResult('error'); setTimeout(() => setResult(null), 4000); return; }

      const giftAtTime = giftCurrent || 'Gain';

      for (const d of snap.docs) {
        const refDoc = d.ref;
        const data = d.data() || {};
        const current = data.compteur || 0;
        const total = current + increment;

        // ⚡️ optimisation écriture : un seul updateDoc
        if (total < multiple) {
          await updateDoc(refDoc, {
            clientUid: refDoc.id,
            code: deleteField(),
            compteur: total,
            timestamps: arrayUnion({ date: new Date(), uid: currentUser.uid, count: increment })
          });
        } else {
          const reste = total - multiple;
          await updateDoc(refDoc, {
            clientUid: refDoc.id,
            code: deleteField(),
            compteur: reste,
            resetstamp: arrayUnion({ date: new Date(), uid: currentUser.uid, count: increment, giftAtTime })
          });
        }

        const unsub = onSnapshot(refDoc, { includeMetadataChanges: true }, (docSnap) => {
          if (!docSnap.metadata.hasPendingWrites) {
            setCodeInput('');
            setFidelityIncrement(1);
            triggerSuccess();
            unsub();
          }
        });
      }
    } catch (error) {
      console.error('Erreur handleValider :', error);
      setResult('error'); setTimeout(() => setResult(null), 4000);
    }
  };

  /* ──────────────── Statistiques (scans ou points selon Type) ──────────────── */
  const stats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const day = (now.getDay() + 6) % 7; // lundi = 0
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
    startOfWeek.setHours(0, 0, 0, 0);

    let scansToday = 0, scansWeek = 0, scansTotal = 0;
    let ptsToday = 0, ptsWeek = 0, ptsTotal = 0;

    for (const row of filteredHistory) {
      const d = row.date instanceof Date ? row.date : new Date(row.date);
      if (row.type === 'timestamp' || row.type === 'resetstamp') {
        const count = parseInt(row.count || 1, 10) || 1;
        scansTotal += count;
        if (d >= startOfToday) scansToday += count;
        if (d >= startOfWeek) scansWeek += count;
      }
      if (row.type === 'pointshistory' && parseFlexibleNumber(row.points) > 0) {
        const pts = parseFlexibleNumber(row.points);
        ptsTotal += pts;
        if (d >= startOfToday) ptsToday += pts;
        if (d >= startOfWeek) ptsWeek += pts;
      }
    }
    return { scansToday, scansWeek, scansTotal, ptsToday, ptsWeek, ptsTotal };
  }, [filteredHistory]);

  // Suffixe selon le type
  const suffix = (fidelityType === 'Fidbypoints') ? 'pts' : ' scans';

  /* ──────────────── Rendu ──────────────── */
  const isCompact = isMobile || isMid;
  const statValue = (pts, scans) => (fidelityType === 'Fidbypoints' ? `${formatPts(pts)} ${suffix.trim()}` : `${scans}${suffix}`);

  return (
    <div>
      <CashierNavbar />
      {showSuccessAnimation && (
        <div className="success-overlay">
          <div className="success-card">
            <div className="success-check">
              <FaCheckCircle />
            </div>
            <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Fidélité validée</h4>
          </div>
        </div>
      )}
      <div className="dashboard-container">
        <style>{`
          .cd-page { width: 100%; display: flex; flex-direction: column; gap: 24px; }
          .cd-page .eb-page-header { margin-bottom: 0; }
          .cd-page .eb-page-title { margin: 0; text-align: left; }

          /* ====== Carte de validation (zone principale caisse) ====== */
          .cd-scan { padding: 28px; display: flex; flex-direction: column; gap: 18px; }
          .cd-scan__label { font-size: 15px; font-weight: 600; color: var(--eb-text-2); margin: 0; }
          .cd-scan__row { display: flex; gap: 12px; align-items: stretch; }
          .cd-scan__input {
            flex: 1; min-width: 0; min-height: 64px; padding: 0 20px;
            font-size: 24px; font-weight: 600; letter-spacing: 0.08em;
            border: 1.5px solid var(--eb-border-strong); border-radius: var(--eb-radius);
            background: var(--eb-surface); color: var(--eb-text);
            box-shadow: var(--eb-shadow-xs);
          }
          .cd-scan__input::placeholder { font-size: 17px; font-weight: 400; letter-spacing: 0; }
          .cd-scan__input:focus { outline: none; border-color: var(--eb-brand-500); box-shadow: var(--eb-ring); }
          .cd-scan__btn { min-height: 64px; min-width: 160px; font-size: 17px; border-radius: var(--eb-radius); }
          .cd-pending { display: flex; flex-direction: column; gap: 8px; }
          .cd-pending__title { font-size: 13px; font-weight: 600; color: var(--eb-muted); text-transform: uppercase; letter-spacing: 0.05em; }
          .cd-pending__list { display: flex; flex-wrap: wrap; gap: 8px; }
          .cd-chip {
            min-height: 44px; padding: 0 16px; border-radius: var(--eb-radius-pill);
            border: 1px solid var(--eb-brand-300); background: var(--eb-brand-50); color: var(--eb-brand);
            font-size: 16px; font-weight: 600; letter-spacing: 0.04em; cursor: pointer;
            transition: background-color .15s ease, border-color .15s ease;
          }
          .cd-chip:hover { background: var(--eb-brand-100); border-color: var(--eb-brand-500); }
          .cd-feedback { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 600; padding: 14px 16px; }
          .cd-feedback svg { flex-shrink: 0; font-size: 20px; }
          .cd-feedback--warning { background: var(--eb-warning-bg); border-color: #fde3b0; color: var(--eb-warning); }

          /* ====== Stats ====== */
          .cd-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
          .cd-stat { padding: 20px; display: flex; flex-direction: column; gap: 6px; }
          .cd-stat__label { font-size: 13px; font-weight: 600; color: var(--eb-muted); text-transform: uppercase; letter-spacing: 0.04em; }
          .cd-stat__value { font-size: 26px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); line-height: 1.15; }

          /* ====== Historique ====== */
          .cd-section-title { margin: 0 0 12px; font-size: 17px; font-weight: 650; color: var(--eb-text); text-align: left; }
          .cd-table-wrap { width: 100%; overflow-x: auto; }
          .cd-table { width: 100%; border-collapse: separate; border-spacing: 0; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); overflow: hidden; box-shadow: var(--eb-shadow-sm); font-size: 14px; }
          .cd-table thead th { background: var(--eb-surface-2); color: var(--eb-muted); font-weight: 600; font-size: 12.5px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; border-bottom: 1px solid var(--eb-border); }
          .cd-table th, .cd-table td { padding: 12px 16px; text-align: left; vertical-align: middle; word-break: break-word; }
          .cd-table tbody td { border-bottom: 1px solid var(--eb-border); color: var(--eb-text-2); }
          .cd-table tbody tr:last-child td { border-bottom: 0; }
          .cd-table tbody tr { cursor: pointer; transition: background-color .12s ease; }
          .cd-table tbody tr:hover { background: var(--eb-brand-50); }
          .cd-table tbody tr:focus-visible { outline: 2px solid var(--eb-brand-500); outline-offset: -2px; }
          .cd-table .td-date { color: var(--eb-muted); white-space: nowrap; }
          .cd-table .td-type { font-weight: 600; color: var(--eb-text); }
          .cd-table .td-actions { text-align: right; }
          .cd-actions { display: flex; justify-content: flex-end; align-items: center; gap: 8px; }
          .cd-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: var(--eb-radius-sm); border: 1px solid #fbd0cb; background: var(--eb-surface); color: var(--eb-danger); cursor: pointer; transition: background-color .15s ease; }
          .cd-icon-btn:hover { background: var(--eb-danger-bg); }

          .row-gift { background: var(--eb-warning-bg); }

          .pill { display: inline-flex; align-items: center; gap: 6px; font-weight: 700; padding: 4px 10px; border-radius: var(--eb-radius-pill); border: 1px solid transparent; font-size: 13.5px; white-space: nowrap; }
          .pill-scan { background: var(--eb-brand-50); color: var(--eb-brand); border-color: var(--eb-brand-100); }
          .pill-gift { background: var(--eb-warning-bg); color: var(--eb-warning); border-color: #fde3b0; }
          .pill-pos  { background: var(--eb-success-bg); color: var(--eb-success); border-color: #c6f0da; }
          .pill-neg  { background: var(--eb-danger-bg); color: var(--eb-danger); border-color: #fbd0cb; }

          .cd-empty { padding: 32px 24px; text-align: center; color: var(--eb-muted); font-size: 14.5px; }
          .cd-more { display: flex; justify-content: center; margin-top: 16px; }

          /* ====== Modales ====== */
          .cd-modal-overlay { position: fixed; inset: 0; background: rgba(15,35,56,.55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 9999; }
          .cd-modal { background: var(--eb-surface); padding: 24px; border-radius: var(--eb-radius-xl); width: 100%; max-width: 420px; max-height: calc(100dvh - 32px); overflow-y: auto; box-shadow: var(--eb-shadow-lg); display: flex; flex-direction: column; gap: 16px; }
          .cd-modal__title { margin: 0; font-size: 19px; font-weight: 700; text-align: left; color: var(--eb-text); }
          .cd-modal__input { min-height: 60px; font-size: 26px; font-weight: 600; text-align: center; }
          .cd-modal__actions { display: flex; justify-content: center; gap: 10px; }
          .cd-gain-btn { width: 100%; min-height: 52px; justify-content: space-between; white-space: normal; text-align: left; line-height: 1.3; padding: 10px 16px; }

          /* ====== Medium ====== */
          @media (max-width: 1059px) and (min-width: 771px) {
            .cd-table th, .cd-table td { padding: 10px 12px; }
          }

          /* ====== Mobile ====== */
          @media (max-width: 768px) {
            .cd-page { gap: 20px; }
            .cd-scan { padding: 20px; gap: 14px; }
            .cd-scan__row { flex-direction: column; }
            .cd-scan__input { min-height: 60px; font-size: 22px; text-align: center; }
            .cd-scan__btn { width: 100%; min-height: 58px; }
            .cd-stats { gap: 8px; }
            .cd-stat { padding: 14px 12px; gap: 4px; }
            .cd-stat__label { font-size: 11.5px; letter-spacing: 0.02em; }
            .cd-stat__value { font-size: 19px; }
            .cd-table { table-layout: fixed; }
            .cd-table th, .cd-table td { padding: 10px 8px; font-size: 13.5px; }
            .cd-table thead th { font-size: 11.5px; letter-spacing: 0.02em; }
            .cd-modal__actions { flex-direction: column-reverse; }
            .cd-modal__actions .eb-btn { width: 100%; }
          }
          @media (max-width: 420px) {
            .cd-stat__value { font-size: 17px; }
            .cd-table th, .cd-table td { padding: 10px 6px; }
          }
        `}</style>

        {/* Modales */}
        {showGainsModal && (
          <div className="cd-modal-overlay" role="dialog" aria-modal="true">
            <div className="cd-modal">
              <h3 className="cd-modal__title">Utiliser un gain</h3>
              {currentCustomerGains.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {currentCustomerGains.map((gain, index) => (
                    <button
                      key={index}
                      className="eb-btn eb-btn--success cd-gain-btn"
                      onClick={() => handleUseGain(gain)}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <FaGift aria-hidden="true" />
                        {isCompact ? gain.gift : `Utiliser ${gain.points} pts : ${gain.gift}`}
                      </span>
                      {isCompact && <span style={{ whiteSpace: 'nowrap' }}>{gain.points} pts</span>}
                    </button>
                  ))}
                </div>
              ) : <p className="eb-muted" style={{ margin: 0 }}>Aucun gain disponible.</p>}
              <div className="cd-modal__actions">
                <button className="eb-btn eb-btn--secondary" onClick={() => { setShowGainsModal(false); setCurrentCustomerGains([]); }}>Annuler</button>
              </div>
            </div>
          </div>
        )}

        {showIncrementModal && (
          <div className="cd-modal-overlay" role="dialog" aria-modal="true">
            <div className="cd-modal">
              <h3 className="cd-modal__title">Nombre d&apos;articles</h3>
              <input
                type="number"
                min="1"
                inputMode="numeric"
                value={tempIncrement}
                onChange={(e) => setTempIncrement(sanitize(e.target.value))}
                className="eb-input cd-modal__input"
                aria-label="Nombre d'articles"
              />
              <div className="cd-modal__actions">
                <button className="eb-btn eb-btn--secondary eb-btn--lg" onClick={() => setShowIncrementModal(false)}>Annuler</button>
                <button className="eb-btn eb-btn--primary eb-btn--lg" onClick={handleConfirmIncrement}>Confirmer</button>
              </div>
            </div>
          </div>
        )}

        {showPointsModal && (
          <div className="cd-modal-overlay" role="dialog" aria-modal="true">
            <div className="cd-modal">
              <h3 className="cd-modal__title">Ajouter des points</h3>
              <div className="eb-field">
                <label htmlFor="amountInput" className="eb-label">Montant de l'achat (€)</label>
                <input
                  id="amountInput"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={amountInput}
                  onChange={(e) => setAmountInput(sanitize(e.target.value.replace(',', '.')))}
                  className="eb-input cd-modal__input"
                />
              </div>
              <div className="cd-modal__actions">
                <button className="eb-btn eb-btn--secondary eb-btn--lg" onClick={() => { setShowPointsModal(false); setPalierAtteint(null); }}>Annuler</button>
                <button className="eb-btn eb-btn--primary eb-btn--lg" onClick={handleConfirmPoints}>Confirmer</button>
              </div>
            </div>
          </div>
        )}

        <div className="cd-page">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title">Caisse</h1>
              <p className="eb-page-subtitle">Validez la fidélité de vos clients avec leur code.</p>
            </div>
          </header>

          {/* Barre de validation */}
          <section className="eb-card cd-scan">
            <label htmlFor="cashier-code" className="cd-scan__label">Code fidélité du client</label>
            <div className="cd-scan__row">
              <input
                id="cashier-code"
                type="text"
                placeholder="Entrez le code client"
                value={codeInput}
                onChange={e => setCodeInput(sanitize(e.target.value))}
                onKeyDown={e => { if (e.key === 'Enter') handleOpenIncrementModal(); }}
                className="cd-scan__input"
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              <button
                onClick={handleOpenIncrementModal}
                className="eb-btn eb-btn--primary cd-scan__btn"
                disabled={!isOnline}
                title={!isOnline ? 'Pas de connexion internet' : 'Valider'}
              >
                <FaCheckCircle aria-hidden="true" /> Valider
              </button>
            </div>

            {/* Codes récents non validés */}
            {pendingCodes.length > 0 && (
              <div className="cd-pending">
                <span className="cd-pending__title">Codes récents</span>
                <div className="cd-pending__list">
                  {pendingCodes.map((code, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCodeInput(sanitize(code))}
                      className="cd-chip"
                    >
                      {sanitize(code)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isOnline && (
              <div className="eb-alert cd-feedback cd-feedback--warning" role="status">
                <FaWifi aria-hidden="true" /> Pas de connexion internet
              </div>
            )}
            {result === 'success' && <div className="eb-alert eb-alert--success cd-feedback" role="status"><FaCheckCircle aria-hidden="true" /> Validé</div>}
            {result === 'offline' && <div className="eb-alert cd-feedback cd-feedback--warning" role="alert"><FaTimesCircle aria-hidden="true" /> Pas de connexion</div>}
            {result === 'error' && <div className="eb-alert eb-alert--danger cd-feedback" role="alert"><FaTimesCircle aria-hidden="true" /> Code invalide</div>}
          </section>

          {/* ───────────── Stats ───────────── */}
          <div className="cd-stats">
            <div className="eb-card cd-stat">
              <div className="cd-stat__label">Aujourd&apos;hui</div>
              <div className="cd-stat__value">{statValue(stats.ptsToday, stats.scansToday)}</div>
            </div>
            <div className="eb-card cd-stat">
              <div className="cd-stat__label">Cette semaine</div>
              <div className="cd-stat__value">{statValue(stats.ptsWeek, stats.scansWeek)}</div>
            </div>
            <div className="eb-card cd-stat">
              <div className="cd-stat__label">Total</div>
              <div className="cd-stat__value">{statValue(stats.ptsTotal, stats.scansTotal)}</div>
            </div>
          </div>

          {/* Tableau Historique (filtré sur le caissier courant) */}
          <section>
            <h2 className="cd-section-title">Mes dernières validations</h2>
            {filteredHistory.length > 0 ? (
              <div className="cd-table-wrap">
                <table className="cd-table">
                  <thead>
                    <tr>
                      <th>{fidelityType === 'Fidbypoints' ? 'Points' : 'Scans'}</th>
                      <th>Date</th>
                      <th>Compte</th>
                      {!isCompact && <th>Type</th>}
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAll ? filteredHistory : filteredHistory.slice(0, 10)).map((row, idx) => {
                      const d = (row?.date instanceof Date && !isNaN(row.date)) ? row.date : new Date(0);
                      const formattedDate = isCompact ? formatDDMMYY(d) : formatCompactDateFR(d);
                      const tenMinutes = 10 * 60 * 1000;
                      const isLastTransaction = idx === 0;
                      const isRecent = Date.now() - d < tenMinutes;

                      let typeLabel = '';
                      let value = '';
                      let pillClass = 'pill';
                      let rowClassName = '';

                      if (row.type === 'resetstamp') {
                        typeLabel = `${row.giftAtTime || 'Gain'}`;
                        value = `x${row.count || 1}`;
                        pillClass += ' pill-gift';
                        rowClassName = 'row-gift';
                      } else if (row.type === 'pointshistory') {
                        if (row.points > 0) {
                          typeLabel = 'Achat';
                          value = `+${formatPts(row.points)} pts`;
                          pillClass += ' pill-pos';
                        } else {
                          typeLabel = `${row.gift || 'Gain'}`;
                          value = `${formatPts(row.points)} pts`;
                          pillClass += ' pill-gift';
                          rowClassName = 'row-gift';
                        }
                      } else { // timestamp
                        typeLabel = 'Scan';
                        value = `+${row.count || 1}`;
                        pillClass += ' pill-scan';
                      }

                      let valueCompact = value;
                      if (row.type === 'pointshistory') {
                        const absPts = Math.abs(parseFlexibleNumber(row.points));
                        valueCompact = `${formatPts(absPts)}`;
                      } else if (row.type === 'timestamp' || row.type === 'resetstamp') {
                        valueCompact = String(row.count || 1);
                      }

                      const isGiftRow = row.type === 'resetstamp' || (row.type === 'pointshistory' && !(row.points > 0));

                      const canDelete = isLastTransaction &&
                        (row.type === 'timestamp' || row.type === 'resetstamp' || row.type === 'pointshistory') &&
                        isRecent;

                      const accountDisplay = row.uid === user?.uid ? 'Vous' : sanitize(userPseudos[row.uid] || row.uid);

                      // Pour bouton "Utiliser un gain", il faut total points du client:
                      const currentCustomerData = fidelityHistory.find(item => row.id.startsWith(item.id));
                      const totalCustomerPoints = currentCustomerData?.points || 0;

                      const customerId = currentCustomerData?.id || (row.id || '').split('-')[0];

                      return (
                        <tr
                          key={row.id}
                          className={rowClassName}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleNavigateToDetails(customerId)}
                          onKeyDown={(event) => {
                            if ((event.key === 'Enter' || event.key === ' ') && event.currentTarget === event.target) {
                              event.preventDefault();
                              handleNavigateToDetails(customerId);
                            }
                          }}
                        >
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span className={pillClass}>
                              {isGiftRow ? <FaGift aria-hidden="true" /> : <FaShoppingCart aria-hidden="true" />}
                              {isCompact ? valueCompact : value}
                            </span>
                          </td>
                          <td className="td-date">{formattedDate}</td>
                          <td>{accountDisplay}</td>
                          {!isCompact && (
                            <td className="td-type" title={typeLabel} aria-label={typeLabel}>
                              <span className="label">{typeLabel}</span>
                            </td>
                          )}
                          <td className="td-actions">
                            <div className="cd-actions">
                              {isLastTransaction && fidelityType === 'Fidbypoints' && row.type === 'pointshistory' && row.points > 0 &&
                                (pointsPaliers.length > 0 && totalCustomerPoints >= pointsPaliers[0].points) && (
                                  <button
                                    className="eb-btn eb-btn--success eb-btn--sm"
                                    style={{ minHeight: 40 }}
                                    title="Utiliser un gain"
                                    aria-label="Utiliser un gain"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenGainsModal(customerId);
                                    }}
                                  >
                                    <FaGift aria-hidden="true" />
                                    {!isCompact && 'Utiliser un gain'}
                                  </button>
                                )}
                              {canDelete && (
                                <button
                                  type="button"
                                  className="cd-icon-btn"
                                  title="Supprimer la dernière transaction"
                                  aria-label="Supprimer la dernière transaction"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteLastTransaction(row);
                                  }}
                                >
                                  <FaTrash size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="eb-card cd-empty">Aucun historique.</div>
            )}

            {!showAll && filteredHistory.length > 10 && (
              <div className="cd-more">
                <button type="button" className="eb-btn eb-btn--secondary" onClick={() => setShowAll(true)}>Afficher plus</button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default CashierDashboard;
